"use client";

import dynamic from "next/dynamic";
import { useState } from "react";
import { GoogleContactForm, type GoogleContactFormProps } from "@modes0/ui-components/client";
import { contactFormWidgetContent } from "@/content/business-details/business-content";

const GoogleReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
    ssr: false,
});

type ContactFormWidgetProps = {
    content?: typeof contactFormWidgetContent;
    className?: string;
};

function ContactGrowthPattern() {
    return (
        <div
            aria-hidden="true"
            className="relative mt-8 hidden h-[360px] overflow-hidden rounded-[8px] border border-[rgba(47,170,46,0.14)] bg-white md:block lg:h-[420px]"
            style={{
                backgroundImage:
                    "linear-gradient(135deg, rgba(47,170,46,0.08), rgba(255,255,255,0.96) 46%, rgba(47,170,46,0.12)), radial-gradient(circle at 18px 18px, rgba(47,170,46,0.18) 1.3px, transparent 1.4px)",
                backgroundSize: "auto, 24px 24px",
            }}
        >
            <div className="absolute -left-16 top-10 h-48 w-48 rounded-full bg-[#2faa2e]/10 blur-2xl" />
            <div className="absolute -right-12 bottom-8 h-56 w-56 rounded-full bg-[#2faa2e]/10 blur-2xl" />
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 430 380" fill="none" focusable="false">
                <defs>
                    <linearGradient id="contact-growth-line" x1="42" y1="270" x2="376" y2="96">
                        <stop stopColor="#2faa2e" stopOpacity="0.25" />
                        <stop offset="0.52" stopColor="#2faa2e" stopOpacity="0.75" />
                        <stop offset="1" stopColor="#111827" stopOpacity="0.32" />
                    </linearGradient>
                    <linearGradient id="contact-growth-fill" x1="120" y1="86" x2="300" y2="284">
                        <stop stopColor="#ffffff" />
                        <stop offset="1" stopColor="#effaf0" />
                    </linearGradient>
                </defs>

                <path
                    d="M42 286C104 228 142 258 184 196C226 134 252 122 310 140C350 152 364 124 386 84"
                    stroke="url(#contact-growth-line)"
                    strokeWidth="6"
                    strokeLinecap="round"
                />
                <path
                    d="M68 305C130 250 174 276 219 215C259 160 290 165 344 172"
                    stroke="#2faa2e"
                    strokeDasharray="8 14"
                    strokeLinecap="round"
                    strokeOpacity="0.24"
                    strokeWidth="3"
                />

                <g transform="translate(154 92)">
                    <circle
                        cx="76"
                        cy="76"
                        r="74"
                        fill="url(#contact-growth-fill)"
                        stroke="#2faa2e"
                        strokeOpacity="0.18"
                    />
                    <path d="M76 122V65" stroke="#2faa2e" strokeLinecap="round" strokeWidth="6" />
                    <path
                        d="M76 76C46 72 32 48 36 24C64 28 80 46 76 76Z"
                        fill="#ffffff"
                        stroke="#2faa2e"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="5"
                    />
                    <path
                        d="M77 82C110 77 128 54 126 27C94 32 74 52 77 82Z"
                        fill="#ffffff"
                        stroke="#2faa2e"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="5"
                    />
                    <path
                        d="M76 116C104 112 121 97 126 75C98 76 80 90 76 116Z"
                        fill="#ffffff"
                        stroke="#2faa2e"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="5"
                    />
                    <path
                        d="M76 109C51 105 35 91 30 70C56 70 73 84 76 109Z"
                        fill="#ffffff"
                        stroke="#2faa2e"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="5"
                    />
                </g>

                <g transform="translate(42 236)">
                    <rect
                        width="92"
                        height="64"
                        rx="8"
                        fill="#ffffff"
                        stroke="#2faa2e"
                        strokeOpacity="0.18"
                        strokeWidth="2"
                    />
                    <circle cx="34" cy="31" r="13" stroke="#2faa2e" strokeWidth="4" />
                    <path d="M44 42L57 53" stroke="#2faa2e" strokeLinecap="round" strokeWidth="4" />
                    <path d="M68 19H76" stroke="#111827" strokeLinecap="round" strokeOpacity="0.24" strokeWidth="4" />
                    <path d="M68 32H78" stroke="#111827" strokeLinecap="round" strokeOpacity="0.18" strokeWidth="4" />
                </g>

                <g transform="translate(294 58)">
                    <rect
                        width="86"
                        height="62"
                        rx="8"
                        fill="#ffffff"
                        stroke="#2faa2e"
                        strokeOpacity="0.18"
                        strokeWidth="2"
                    />
                    <path d="M22 42V30" stroke="#2faa2e" strokeLinecap="round" strokeWidth="5" />
                    <path d="M42 42V20" stroke="#2faa2e" strokeLinecap="round" strokeWidth="5" />
                    <path d="M62 42V26" stroke="#2faa2e" strokeLinecap="round" strokeWidth="5" />
                    <path d="M17 46H68" stroke="#111827" strokeLinecap="round" strokeOpacity="0.22" strokeWidth="4" />
                </g>

                <g transform="translate(284 258)">
                    <rect
                        width="96"
                        height="68"
                        rx="8"
                        fill="#ffffff"
                        stroke="#2faa2e"
                        strokeOpacity="0.18"
                        strokeWidth="2"
                    />
                    <path
                        d="M22 22H74V50H48L34 61V50H22V22Z"
                        fill="#effaf0"
                        stroke="#2faa2e"
                        strokeLinejoin="round"
                        strokeWidth="4"
                    />
                    <circle cx="39" cy="36" r="3" fill="#2faa2e" />
                    <circle cx="50" cy="36" r="3" fill="#2faa2e" />
                    <circle cx="61" cy="36" r="3" fill="#2faa2e" />
                </g>

                <circle cx="64" cy="96" r="5" fill="#2faa2e" fillOpacity="0.45" />
                <circle cx="96" cy="126" r="3" fill="#111827" fillOpacity="0.16" />
                <circle cx="354" cy="214" r="5" fill="#2faa2e" fillOpacity="0.35" />
                <circle cx="392" cy="240" r="3" fill="#111827" fillOpacity="0.16" />
            </svg>
        </div>
    );
}

export default function ContactFormWidget({ content = contactFormWidgetContent, className }: ContactFormWidgetProps) {
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [captchaKey, setCaptchaKey] = useState(0);
    const [formResetNonce, setFormResetNonce] = useState(0);
    const captchaSiteKey = content.captcha.siteKey;
    const formConfig = content.form as GoogleContactFormProps;

    const resetCaptcha = () => {
        setCaptchaToken(null);
        setCaptchaKey((key) => key + 1);
    };

    const handleSuccess: GoogleContactFormProps["onSuccess"] = (response, payload) => {
        formConfig.onSuccess?.(response, payload);
        setFormResetNonce((nonce) => nonce + 1);
    };

    return (
        <section
            id={content.section.id}
            className={className || "bg-white px-4 py-12"}
            aria-labelledby={content.section.titleId}
        >
            <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[0.85fr_1.15fr] md:items-start">
                <div className="pt-2">
                    <p className="text-sm font-bold uppercase text-[#2faa2e]">{content.section.eyebrow}</p>
                    <h2 id={content.section.titleId} className="mt-2 text-3xl font-bold text-neutral-950 md:text-4xl">
                        {content.section.title}
                    </h2>
                    <p className="mt-4 text-base leading-7 text-neutral-700">{content.section.description}</p>
                    <ContactGrowthPattern />
                </div>

                <div className="flex justify-center md:justify-end">
                    <GoogleContactForm
                        {...formConfig}
                        className="grow-leads-contact-form"
                        initialValues={{
                            ...(formConfig.initialValues ?? {}),
                            __resetNonce: formResetNonce,
                        }}
                        onSuccess={handleSuccess}
                        captchaElement={
                            captchaSiteKey ? (
                                <div className="grow-leads-recaptcha-field">
                                    <div className="grow-leads-recaptcha-copy">
                                        <span className="grow-leads-recaptcha-label">Security check</span>
                                        <span className="grow-leads-recaptcha-note">
                                            Tick the box before sending your enquiry.
                                        </span>
                                    </div>
                                    <GoogleReCAPTCHA
                                        key={captchaKey}
                                        sitekey={captchaSiteKey}
                                        onChange={(token) => setCaptchaToken(token)}
                                        onExpired={() => setCaptchaToken(null)}
                                        onErrored={() => setCaptchaToken(null)}
                                    />
                                </div>
                            ) : undefined
                        }
                        getCaptchaToken={captchaSiteKey ? () => captchaToken : undefined}
                        resetCaptcha={captchaSiteKey ? resetCaptcha : undefined}
                    />
                </div>
            </div>
        </section>
    );
}
